/**
 * `/xizmat/:code` — integratsiya shlyuzining qaytish nuqtasi (ЧТЗ 4.2.11.5).
 *
 * OneID orqali avtorizatsiyadan so'ng foydalanuvchi shu sahifaga qaytadi:
 * shlyuz qayta chaqiriladi (endi sessiya bor) va foydalanuvchi so'ragan
 * xizmatga — ichki bo'limga yoki tashqi davlat xizmatiga (SSO token bilan)
 * — avtomatik o'tkaziladi.
 */
import { useEffect, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";

import { ApiError } from "@/shared/api/client";
import { portalApi } from "@/shared/api/endpoints";
import type { ServiceContext } from "@/shared/api/types";
import { useI18n } from "@/shared/i18n";
import { SECTION_PATHS } from "@/shared/lib/sectionRoutes";
import { BrandMark, Icon } from "@/shared/ui/Icon";

import { gatewayReturnPath, openServiceUrl } from "./landing/ServiceGateway";

export function ServiceGatewayPage() {
  const { code = "" } = useParams();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { t } = useI18n();
  const [error, setError] = useState("");
  /* Uchastka raqami tashqi tizimga uzatilmasa — o'tishdan oldin ko'rsatiladi. */
  const [plotNotice, setPlotNotice] = useState<{ url: string; plot: string } | null>(null);
  const query = params.toString();

  useEffect(() => {
    let cancelled = false;
    const search = new URLSearchParams(query);
    const context: ServiceContext = {};
    for (const key of ["plot", "purpose", "lat", "lon"] as const) {
      const value = search.get(key);
      if (value) context[key] = value;
    }
    portalApi
      .serviceRedirect(code, context.plot ? gatewayReturnPath(code, context) : "", context)
      .then((result) => {
        if (cancelled) return;
        if (!result.requires_login && result.context === "not_supported" && context.plot) {
          setPlotNotice({ url: result.url, plot: context.plot });
          return;
        }
        openServiceUrl(result.url, (to) => navigate(to, { replace: true }));
      })
      .catch((caught: unknown) => {
        if (cancelled) return;
        setError(
          caught instanceof ApiError && !caught.isOffline
            ? caught.message
            : t("auth.err.network"),
        );
      });
    return () => {
      cancelled = true;
    };
  }, [code, query, navigate, t]);

  return (
    <div className="auth-panel" style={{ minHeight: "100vh", alignItems: "center", justifyContent: "center" }}>
      <div className="auth-form-wrap" style={{ textAlign: "center" }}>
        <div style={{ display: "flex", justifyContent: "center", marginBottom: 20 }}>
          <BrandMark size={48} />
        </div>
        {error ? (
          <>
            <div className="alert alert-error" role="alert">
              <Icon name="alert" size={18} />
              <span>{error}</span>
            </div>
            <Link className="btn btn-outline btn-block" to={SECTION_PATHS.services} style={{ marginTop: 16 }}>
              Xizmatlar ro'yxatiga qaytish
            </Link>
          </>
        ) : plotNotice ? (
          <div className="plot-notice" style={{ textAlign: "left" }}>
            <div className="plot-notice-id">
              <span>Uchastka / maydon</span>
              <b className="num">{plotNotice.plot}</b>
            </div>
            <p>
              Xizmat tizimi hozircha uchastka ma'lumotlarini portaldan avtomatik qabul qilmaydi.
              Arizani to'ldirishda shu raqam yoki nomni kiriting yoki maydonni o'sha tizim xaritasida tanlang.
            </p>
            <button
              type="button"
              className="btn btn-primary btn-block"
              onClick={() => openServiceUrl(plotNotice.url, (to) => navigate(to, { replace: true }))}
            >
              <Icon name="external" size={16} />
              Xizmatga o'tish
            </button>
          </div>
        ) : (
          <p className="muted" role="status">
            Xizmatga yo'naltirilmoqda…
          </p>
        )}
      </div>
    </div>
  );
}

/**
 * OneID «sandbox» rejimi (kalitlar sozlanmagan kontur): haqiqiy OneID sahifasi
 * o'rniga shu yerga keladi va sinov kodi bilan callback'ga o'tadi.
 */
export function OneIdSandboxPage() {
  const navigate = useNavigate();

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const state = params.get("state") ?? "";
    const timer = window.setTimeout(() => {
      navigate(`/auth/oneid/callback?code=sandbox&state=${encodeURIComponent(state)}`, {
        replace: true,
      });
    }, 600);
    return () => window.clearTimeout(timer);
  }, [navigate]);

  return (
    <div className="auth-panel" style={{ minHeight: "100vh", alignItems: "center", justifyContent: "center" }}>
      <div className="auth-form-wrap" style={{ textAlign: "center" }}>
        <div style={{ display: "flex", justifyContent: "center", marginBottom: 20 }}>
          <BrandMark size={48} />
        </div>
        <p className="alert alert-info" role="status">
          <Icon name="info" size={18} />
          <span>OneID sinov rejimi: kalitlar sozlanmagan, sinov foydalanuvchisi bilan kirilmoqda…</span>
        </p>
      </div>
    </div>
  );
}
